/* MaterialSubmissionsPage */
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './PageLayout.css';
import { auth } from '../firebaseConfig';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { API_BASE_URL, fetchNewMaterialCategories } from '../services/api';

// ✅ SUCCESS MODAL - Updated for Material Approval (Admin context)
const SuccessModal = ({ isOpen, onClose, materialName, emailSent }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">
            ✅
          </div>
          <h2>Product Approved Successfully!</h2>
        </div>
        <div className="success-modal-body">
          <p><strong>{materialName}</strong> has been approved and is now available on the platform.</p>
          <div className="request-details">
            <p>{emailSent ? '📧 Notification email sent successfully' : '⚠️ Email notification failed (but approval succeeded)'}</p>
            <p>The product is now accessible to users for their projects.</p>
          </div>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const MaterialSubmissionsPage = () => {
  console.log('🔍 MaterialSubmissionsPage started rendering');
  
  const navigate = useNavigate();
  
  // ✅ Use proper context instead of hardcoded values
  const { user } = useContext(AuthContext);
  const { selectedRegion } = useRegion();
  const role = user?.role || 'admin';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categorized, setCategorized] = useState({
    pending: [],
    approved: [],
    rejected: [],
    dismissed: [],
  });

  const [activeTab, setActiveTab] = useState('pending');
  
  // States for detailed modal
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // ✅ SUCCESS MODAL STATES
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({
    materialName: '',
    emailSent: false
  });

  const [completingId, setCompletingId] = useState(null);

  // Category id → name map so we show the readable name instead of the UUID.
  const [categoryMap, setCategoryMap] = useState({});
  const resolveCategory = (cat) => (cat ? (categoryMap[cat] || cat) : '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await fetchNewMaterialCategories();
        if (cancelled) return;
        const map = {};
        (Array.isArray(cats) ? cats : []).forEach((c) => {
          if (c?.id) map[c.id] = c.name || c.id;
        });
        setCategoryMap(map);
      } catch (e) {
        console.error('❌ Error loading category names:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegion]);

  console.log('🔍 Component state initialized');

  useEffect(() => {
    console.log('🔍 useEffect triggered, loading submissions...');
    if (user && selectedRegion) {
      loadSubmissions();
    }
  }, [user, selectedRegion]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user || !selectedRegion) return;

      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated. Please log in.');

      const regionNameMap = {
        'vquest': 'New Zealand',
        'vquest-au-1': 'Australia',
        'vquest-uk-1': 'United Kingdom',
        'vquest-uk-ICE': 'UK - ICE',
        'vquest-us-1': 'USA/Canada',
        'vquest-irl-1': 'Ireland',
        'vquest-midrise': 'Mid Rise',
      };

      const selectedRegionName = regionNameMap[selectedRegion] || selectedRegion;

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-user-role': role,
        'x-user-id': user.uid,
        'x-user-email': user.email,
        'x-region': selectedRegion,
      };

      const response = await fetch(`${API_BASE_URL}/material-requests/all`, { headers });

      if (!response.ok) throw new Error(`API failed: ${response.status}`);

      const data = await response.json();

      const requests = Array.isArray(data?.requests) ? data.requests : [];

      // Match on the stable RegionId code first (e.g. "vquest-au-1"), since the
      // display RegionName stored at submission time may differ from the admin
      // map or may be missing/null. Fall back to a RegionName match for legacy
      // rows that only carried a name.
      const norm = (v) => (v ?? '').toString().trim().toLowerCase();
      const wantId = norm(selectedRegion);
      const wantName = norm(selectedRegionName);

      const filtered = requests.filter(r => {
        const rId = norm(r.RegionId);
        const rName = norm(r.RegionName);
        return rId === wantId || rName === wantName || rName === wantId;
      });

      console.log('🔍 Region filter:', {
        selectedRegion, selectedRegionName,
        total: requests.length, matched: filtered.length,
        sample: requests.slice(0, 5).map(r => ({ RegionId: r.RegionId, RegionName: r.RegionName, Status: r.Status })),
      });

      setCategorized({
        pending: filtered.filter(r => r.Status === 'Pending'),
        approved: filtered.filter(r => r.Status === 'Approved'),
        rejected: filtered.filter(r => r.Status === 'Rejected'),
        dismissed: filtered.filter(r => r.Status === 'Dismissed'),
      });

    } catch (error) {
      console.error('❌ Error in loadSubmissions:', error);
      setError(error.message);
      setCategorized({
        pending: [],
        approved: [],
        rejected: [],
        dismissed: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const moveItem = (id, from, to) => {
    setCategorized(prev => {
      const item = prev[from].find(r => r.RequestId === id);
      if (!item) return prev;

      return {
        ...prev,
        [from]: prev[from].filter(r => r.RequestId !== id),
        [to]: [...prev[to], { ...item, Status: to.charAt(0).toUpperCase() + to.slice(1) }]
      };
    });
  };

  // Backend API email function
  const sendMaterialApprovalEmail = async (submission) => {
    try {
      console.log(`📧 Sending material approval email via backend API for: ${submission.MaterialData?.Name}`);
      
      // ✅ Get Firebase token
      const token = await auth.currentUser?.getIdToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please log in.');
      }
      
      const requestData = {
        submitterEmail: submission.SubmittedByEmail,
        submitterName: submission.SubmittedByEmail?.split('@')[0] || 'User',
        submitterRole: submission.SubmittedByRole || 'user',
        materialName: submission.MaterialData?.Name || 'Unknown Material',
        materialDescription: submission.MaterialData?.Description || 'No description provided',
        materialCategory: resolveCategory(submission.MaterialData?.CategoryId) || 'Uncategorized',
        regionName: submission.RegionName || 'Unknown Region',
        requestId: submission.RequestId,
        submittedDate: submission.SubmittedAt ? new Date(submission.SubmittedAt).toLocaleDateString() : 'Unknown',
        approvedDate: new Date().toLocaleDateString(),
        adminName: user.email || 'Admin Team'
      };

      console.log('📧 Backend API request data:', requestData);

      const response = await fetch(`${API_BASE_URL}/email/send-material-approval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-user-role': role,
          'x-user-id': user.uid,
          'x-user-email': user.email,
          'x-region': selectedRegion,
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('✅ Material approval email sent successfully via backend API');
        return true;
      } else {
        console.error('❌ Backend API error:', result.message);
        return false;
      }
      
    } catch (error) {
      console.error('❌ Error sending material approval email:', error);
      return false;
    }
  };

  const markSubmissionCompleted = async (requestId) => {
    try {
      setCompletingId(requestId);

      // ✅ Get Firebase token
      const token = await auth.currentUser?.getIdToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please log in.');
      }

      // Get the submission details before approval for email
      const submission = selectedSubmission || categorized.pending.find(s => s.RequestId === requestId);

      const res = await fetch(`${API_BASE_URL}/material-requests/${encodeURIComponent(requestId)}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-user-role': role,
          'x-user-id': user.uid,
          'x-user-email': user.email,
          'x-region': selectedRegion,
        },
        body: JSON.stringify({ status: 'Approved' }),
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Failed to complete: ${res.status} ${res.statusText} - ${t}`);
      }

      console.log('✅ Material submission approved in database');

      // Send approval email notification via backend API
      let emailSent = false;
      if (submission && submission.SubmittedByEmail) {
        console.log('📧 Sending approval email notification via backend API...');
        emailSent = await sendMaterialApprovalEmail(submission);
        
        if (emailSent) {
          console.log('✅ Approval email sent successfully');
        } else {
          console.warn('⚠️ Approval email failed to send');
        }
      } else {
        console.warn('⚠️ No submission data or email found, skipping email notification');
      }

      // Update UI immediately
      moveItem(requestId, 'pending', 'approved');
      closeDetailModal(); 

      // ✅ SHOW SUCCESS MODAL
      setSuccessModalData({
        materialName: submission?.MaterialData?.Name || 'Material',
        emailSent: emailSent
      });
      setShowSuccessModal(true);

    } catch (e) {
      console.error('❌ markSubmissionCompleted error', e);
      alert(e.message || 'Failed to mark as completed');
    } finally {
      setCompletingId(null);
    }
  };

  const rejectSubmission = async (requestId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated. Please log in.');

      const res = await fetch(`${API_BASE_URL}/material-requests/${encodeURIComponent(requestId)}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-user-role': role,
          'x-user-id': user.uid,
          'x-user-email': user.email,
          'x-region': selectedRegion,
        },
        body: JSON.stringify({ status: 'Rejected' }),
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Failed to reject: ${res.status} - ${t}`);
      }

      moveItem(requestId, 'pending', 'rejected');

      if (selectedSubmission?.RequestId === requestId) {
        setSelectedSubmission(prev => prev ? { ...prev, Status: 'Rejected' } : prev);
        setShowDetailModal(false);
        setSelectedSubmission(null);
      }
    } catch (e) {
      console.error('❌ rejectSubmission error', e);
      alert(e.message || 'Failed to reject submission');
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessModalData({ materialName: '', emailSent: false });
  };

  const openDetailModal = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSubmission(null);
  };

  const dismiss = async (requestId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated. Please log in.');

      const res = await fetch(`${API_BASE_URL}/material-requests/${encodeURIComponent(requestId)}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-user-role': role,
          'x-user-id': user.uid,
          'x-user-email': user.email,
          'x-region': selectedRegion,
        },
        body: JSON.stringify({ status: 'Dismissed' }),
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Failed to dismiss: ${res.status} - ${t}`);
      }

      moveItem(requestId, 'pending', 'dismissed');

      if (selectedSubmission?.RequestId === requestId) {
        setShowDetailModal(false);
        setSelectedSubmission(null);
      }
    } catch (e) {
      console.error('❌ dismiss error', e);
      alert(e.message || 'Failed to dismiss submission');
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
    try {
      console.log('Downloading via backend proxy:', fileName);
      
      const proxyUrl = `${API_BASE_URL}/material-requests/download-file?fileUrl=${encodeURIComponent(fileUrl)}&fileName=${encodeURIComponent(fileName || 'download')}`;
      
      console.log('Proxy URL:', proxyUrl);
      
      const link = document.createElement('a');
      link.href = proxyUrl;
      link.download = fileName || 'download';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Download initiated via proxy');
      
    } catch (error) {
      console.error('❌ Error with proxy download:', error);
      alert('Download failed: ' + error.message);
    }
  };

  // Add this helper function at the top of the component
  const getAuthenticatedUrl = async (baseUrl) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Add token as query parameter
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  };

  const buildProductPrefill = (s) => {
    const md = s?.MaterialData || {};
    const env = s?.EnvironmentalData || {};
    const num = (v) => (v === '' || v == null ? '' : Number(v));

    return {
      Name: md.Name || '',
      CategoryName: md.CategoryName || md.Category || '',
      CategoryId: md.CategoryId || '',
      QuantityTypeName: md.QuantityTypeName || md.QuantityType || '',
      QuantityTypeId: md.QuantityTypeId || '',
      Vendor: md.Vendor || s?.SubmittedByVendor || '',
      SKU: md.SKU || '',
      CostValue: num(env?.cost?.value),
      Co2Value: num(env?.carbon?.value),
      BiogenicValue: num(env?.biogenic?.value),
      WeightValue: num(env?.weight?.value),
    };
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return '🖼️';
      case 'txt': return '📋';
      default: return '📁';
    }
  };

  console.log('🔍 About to render, current state:', {
    loading,
    error,
    pendingCount: categorized.pending.length,
    approvedCount: categorized.approved.length,
    rejectedCount: categorized.rejected.length,
    dismissedCount: categorized.dismissed.length
  });

  // ✅ Add loading state for user/region
  if (!user || !selectedRegion) {
    return (
      <div style={{ padding: '20px' }}>
        <button onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        <h1>Product Submissions</h1>
        <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: '5px' }}>
          <p>Loading authentication...</p>
          <div>🔄 Please wait...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <button onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        <h1>Material Submissions - Error</h1>
        <div style={{ background: '#ffebee', padding: '15px', borderRadius: '5px', color: '#c62828' }}>
          <h3>Error Loading Data:</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <button onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        <h1>Product Submissions</h1>
        <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: '5px' }}>
          <p>Loading submissions...</p>
          <div>🔄 Please wait...</div>
        </div>
      </div>
    );
  }

  const TABS = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'dismissed', label: 'Dismissed' },
  ];

  const currentSubmissions = categorized[activeTab] || [];

  return (
    <div style={{ padding: '20px' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <button onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        </div>
        <h1>Product Submissions</h1>
        <p>Review and manage product requests from suppliers and users</p>
        <br />

        <div style={{ marginBottom: '20px' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                marginRight: '10px',
                padding: '10px 15px',
                backgroundColor: activeTab === tab.key ? '#2f8e81' : '#f5f5f5',
                color: activeTab === tab.key ? 'white' : 'black',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              {tab.label} ({categorized[tab.key].length})
            </button>
          ))}
        </div>
      </div>

      <div>
        {currentSubmissions.length === 0 ? (
          <div style={{ background: '#f5f5f5', padding: '30px', textAlign: 'center', borderRadius: '5px' }}>
            <h3>No submissions found</h3>
            <p>
              {activeTab  === 'pending' 
                ? 'There are no pending material requests at this time.' 
                : 'No material submissions have been made yet.'}
            </p>
          </div>
        ) : (
          <div>
            <h3>{TABS.find(t => t.key === activeTab)?.label} Submissions:</h3>
            {currentSubmissions.map((submission, index) => (
              <div key={submission.RequestId || index} style={{ 
                border: '1px solid #ddd', 
                margin: '10px 0', 
                padding: '15px', 
                borderRadius: '5px',
                backgroundColor: '#fafafa'
              }}>
                <h4>{submission.MaterialData?.Name || 'Unknown Material'}</h4>
                <p><strong>Status:</strong> {submission.Status}</p>
                <p><strong>Submitted by:</strong> {submission.SubmittedByEmail}</p>
                <p><strong>Role:</strong> {submission.SubmittedByRole}</p>
                <p><strong>Region:</strong> {submission.RegionName}</p>
                <p><strong>Date:</strong> {new Date(submission.SubmittedAt).toLocaleString()}</p>
                
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'center' }}>
                  <button 
                    onClick={() => openDetailModal(submission)}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#2f8e81',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    View Full Details
                  </button>
                  <button
                    onClick={() => rejectSubmission(submission.RequestId)}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#c62828',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    ❌ Reject
                  </button>
                  <button 
                    onClick={() => dismiss(submission.RequestId)}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#888',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️ Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        materialName={successModalData.materialName}
        emailSent={successModalData.emailSent}
      />

      {showDetailModal && selectedSubmission && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto',
            width: '800px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '10px'
            }}>
              <h2>📋 {selectedSubmission.MaterialData?.Name || 'Material Details'}</h2>
              <button 
                onClick={closeDetailModal}
                style={{
                  background: 'none',
                  color: 'red',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3>🏗️ Material Information</h3>
              <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px' }}>
                <p><strong>Name:</strong> {selectedSubmission.MaterialData?.Name || 'N/A'}</p>
                <p><strong>Description:</strong> {selectedSubmission.MaterialData?.Description || 'N/A'}</p>
                <p><strong>Category:</strong> {resolveCategory(selectedSubmission.MaterialData?.CategoryId) || 'N/A'}</p>
                <p><strong>Quantity Type:</strong> {selectedSubmission.MaterialData?.QuantityTypeName || selectedSubmission.MaterialData?.QuantityType || 'N/A'}</p>
                <p>
                  <strong>Color:</strong> 
                  <span style={{ 
                    backgroundColor: selectedSubmission.MaterialData?.ColorHex || '#cccccc',
                    padding: '2px 8px',
                    marginLeft: '5px',
                    borderRadius: '3px',
                    color: 'white'
                  }}>
                    {selectedSubmission.MaterialData?.ColorHex || 'N/A'}
                  </span>
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3>🌍 Environmental Impact Data</h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '10px' 
              }}>
                {selectedSubmission.EnvironmentalData && Object.entries(selectedSubmission.EnvironmentalData).map(([key, data]) => (
                  <div key={key} style={{ 
                    background: '#e3fdee8f', 
                    padding: '15px', 
                    borderRadius: '5px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                      {key === 'carbon' ? '🌡️' : 
                       key === 'biogenic' ? '🌱' : 
                       key === 'weight' ? '⚖️' : 
                       key === 'cost' ? '💰' : '📊'}
                    </div>
                    <strong>{key.charAt(0).toUpperCase() + key.slice(1)}</strong>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#4A9B8F' }}>
                      {data?.value || 'N/A'} {data?.unit || ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3>📁 Uploaded Files</h3>
              <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px' }}>
                {Array.isArray(selectedSubmission.UploadedFiles) && selectedSubmission.UploadedFiles.length > 0 ? (
                  <div>
                    {selectedSubmission.UploadedFiles.map((file, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        marginBottom: '10px',
                        background: 'white'
                      }}>
                        <span style={{ fontSize: '20px' }}>{getFileIcon(file.name)}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold' }}>{file.name || `File ${index + 1}`}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                          </div>
                          <div style={{ fontSize: '10px', color: '#999' }}>
                            Type: {file.type || 'Unknown'}
                          </div>
                        </div>
                        
                        <div>
                          <button 
                            onClick={async () => {
                              try {
                                console.log('Opening file via proxy:', file.url);
                                const proxyUrl = `${API_BASE_URL}/material-requests/view-file?fileUrl=${encodeURIComponent(file.url)}`;
                                const authenticatedUrl = await getAuthenticatedUrl(proxyUrl);
                                window.open(authenticatedUrl, '_blank');
                              } catch (error) {
                                console.error('Error opening file:', error);
                                alert('Failed to open file: ' + error.message);
                              }
                            }}
                            style={{
                              marginRight: '5px',
                              padding: '5px 10px',
                              background: '#4A9B8F',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer'
                            }}
                          >
                            👁️ View
                          </button>

                          <button 
                            onClick={async () => {
                              try {
                                console.log('Downloading file:', file.url, 'with name:', file.name);
                                const proxyUrl = `${API_BASE_URL}/material-requests/download-file?fileUrl=${encodeURIComponent(file.url)}&fileName=${encodeURIComponent(file.name || 'download')}`;
                                const authenticatedUrl = await getAuthenticatedUrl(proxyUrl);
                                
                                const link = document.createElement('a');
                                link.href = authenticatedUrl;
                                link.download = file.name || 'download';
                                link.style.display = 'none';
                                
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                
                                console.log('✅ Download initiated via proxy');
                              } catch (error) {
                                console.error('❌ Error with proxy download:', error);
                                alert('Download failed: ' + error.message);
                              }
                            }}
                            style={{
                              padding: '5px 10px',
                              background: '#305566',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer'
                            }}
                          >
                            📥 Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ textAlign: 'center', color: '#666' }}>No files uploaded</p>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3>📋 Submission Details</h3>
              <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px' }}>
                <p><strong>Request ID:</strong> {selectedSubmission.RequestId}</p>
                <p><strong>Submitted by:</strong> {selectedSubmission.SubmittedByEmail}</p>
                <p><strong>Role:</strong> {selectedSubmission.SubmittedByRole}</p>
                <p><strong>Region:</strong> {selectedSubmission.RegionName}</p>
                <p><strong>Status:</strong> {selectedSubmission.Status}</p>
                <p><strong>Date:</strong> {new Date(selectedSubmission.SubmittedAt).toLocaleString()}</p>
                <p><strong>Email Notifications:</strong> 
                  {selectedSubmission.SubmittedByEmail ? 
                    ` ✅ Will notify ${selectedSubmission.SubmittedByEmail} via backend API` : 
                    ' ❌ No email available'}
                </p>
              </div>
            </div>

            <div style={{ textAlign: 'center', display:'flex', gap:12, justifyContent:'center' }}>
              <button
                onClick={() => markSubmissionCompleted(selectedSubmission.RequestId)}
                disabled={completingId === selectedSubmission.RequestId || selectedSubmission.Status === 'Approved'}
                style={{
                  padding: '10px 30px',
                  background: selectedSubmission.Status === 'Approved' ? '#9e9e9e' : '#1565c0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: selectedSubmission.Status === 'Approved' ? 'not-allowed' : 'pointer',
                  opacity: completingId === selectedSubmission.RequestId ? 0.7 : 1,
                }}
              >
                {selectedSubmission.Status === 'Approved'
                  ? '✅ Approved'
                  : (completingId === selectedSubmission.RequestId ? '📧 Approving & Notifying...' : '✅ Approve & Notify')}
              </button>

              <button
                onClick={() => {
                  const prefill = buildProductPrefill(selectedSubmission);
                  localStorage.setItem('newProductPrefill', JSON.stringify(prefill));
                  navigate('/newproduct', { state: { prefill, from: 'material-submission', requestId: selectedSubmission.RequestId }});
                }}
                style={{
                  padding: '10px 30px',
                  background: '#27786d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Add Product
              </button>

              <button
                onClick={() => rejectSubmission(selectedSubmission.RequestId)}
                disabled={selectedSubmission.Status === 'Rejected'}
                style={{
                  padding: '10px 30px',
                  background: selectedSubmission.Status === 'Rejected' ? '#9e9e9e' : '#c62828',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: selectedSubmission.Status === 'Rejected' ? 'not-allowed' : 'pointer',
                }}
              >
                {selectedSubmission.Status === 'Rejected' ? '❌ Rejected' : '❌ Reject'}
              </button>

              <button
                onClick={closeDetailModal}
                style={{ padding:'10px 30px', background:'#666', color:'#fff', border:'none', borderRadius:5, cursor:'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialSubmissionsPage;